import * as Cesium from "cesium";

export const toggleHeightFilteredPoints = (viewer, dataSources, minHeight, maxHeight) => {
  // Check if "highlightedPoints" data source already exists, and remove it if it does
  const existing = viewer.dataSources.getByName("highlightedPoints")[0];
  if (existing) {
    viewer.dataSources.remove(existing, true);
    return; // Exit if the existing data source is removed
  }

  const now = Cesium.JulianDate.now();
  const highlightedPoints = new Cesium.CustomDataSource("highlightedPoints"); // Create a new CustomDataSource
  const pointsToAdd = []; // Array to hold points within the height range

  // Loop through each data source to process the entities
  dataSources.forEach((ds) => {
    const entities = ds.entities.values;

    // Iterate through all entities in the current data source
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      let positions = [];

      // Check if the entity has a position
      if (entity.position) {
        const pos = entity.position.getValue(now);
        if (pos) positions.push(pos);
      }
      // Check if the entity has a polyline with positions
      else if (entity.polyline?.positions) {
        const pos = entity.polyline.positions.getValue(now);
        if (pos) positions = pos;
      }
      // Check if the entity has a polygon hierarchy with positions
      else if (entity.polygon?.hierarchy) {
        const hierarchy = entity.polygon.hierarchy.getValue(now);
        if (hierarchy?.positions) positions = hierarchy.positions;
      }

      // Iterate through the positions to check height values
      for (let j = 0; j < positions.length; j++) {
        const pos = positions[j];
        const carto = Cesium.Cartographic.fromCartesian(pos); // Convert position to Cartographic coordinates
        const height = carto.height;

        // Check if the height is within the specified range
        if (height >= minHeight && height <= maxHeight) {
          pointsToAdd.push({
            position: Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, height), // Add valid position
          });
        }
      }
    }
  });

  // Suspend events while adding points to prevent unnecessary event handling
  highlightedPoints.entities.suspendEvents();
  for (let i = 0; i < pointsToAdd.length; i++) {
    // Add the points as entities with a red color and black outline
    highlightedPoints.entities.add({
      position: pointsToAdd[i].position,
      point: {
        color: Cesium.Color.RED.withAlpha(0.9),
        pixelSize: 6,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 1,
      },
    });
  }
  // Resume events after adding all points
  highlightedPoints.entities.resumeEvents();

  // Add the highlighted points to the viewer
  viewer.dataSources.add(highlightedPoints);
};

export const getMinMaxZFromDataSources = (dataSources) => {
  let minZ = Number.POSITIVE_INFINITY; // Start with the largest possible number
  let maxZ = Number.NEGATIVE_INFINITY; // Start with the smallest possible number

  const time = Cesium.JulianDate.now(); // Get the current time for evaluating time-dependent values

  // Loop through each data source
  for (const dataSource of dataSources) {
    if (!dataSource) continue;
    const entities = dataSource.entities.values;

    // Loop through each entity in the data source
    for (const entity of entities) {
      let positions = []; // Array to store positions

      // Check if the entity has a point position
      if (entity.position?.getValue) {
        const pos = entity.position.getValue(time); // Get the position at the current time
        if (pos) positions.push(pos); // Add to positions array
      }

      // Check if the entity has a polyline with positions
      if (entity.polyline?.positions?.getValue) {
        const polylinePositions = entity.polyline.positions.getValue(time); // Get polyline positions
        if (Array.isArray(polylinePositions)) {
          positions.push(...polylinePositions); // Add polyline positions to positions array
        }
      }

      // Check if the entity has a polygon with hierarchy (could be complex with holes)
      if (entity.polygon?.hierarchy?.getValue) {
        const hierarchy = entity.polygon.hierarchy.getValue(time);

        // Recursive function to extract all positions from a polygon hierarchy, including holes
        const extractPositions = (h) => {
          let all = [];
          if (h.positions) all.push(...h.positions); // Add positions from the outer ring
          if (h.holes) {
            for (const hole of h.holes) {
              all.push(...extractPositions(hole)); // Add positions from holes
            }
          }
          return all;
        };

        positions.push(...extractPositions(hierarchy)); // Add extracted positions to array
      }

      // Loop through all positions to find the minimum and maximum Z (height)
      for (const pos of positions) {
        const carto = Cesium.Cartographic.fromCartesian(pos); // Convert position to Cartographic coordinates
        const height = carto.height; // Get the height (Z-coordinate)

        // Update min and max Z values based on the height
        if (height < minZ) minZ = height;
        if (height > maxZ) maxZ = height;
      }
    }
  }

  return { minZ, maxZ }; // Return the min and max height (Z) values
};
